import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { resolveImage, formatRM, BACKEND_URL } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { Minus, Plus, Share2, Copy, MessageCircle, ArrowLeft } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null));
  }, [id]);

  if (!product) {
    return (
      <div className="mx-auto max-w-4xl px-6 lg:px-10 py-24 text-[#4a6e77]">
        {t("common.loading")}
      </div>
    );
  }

  const name = zh ? product.name_zh : product.name_en;
  const otherName = zh ? product.name_en : product.name_zh;
  const desc = zh ? product.desc_zh : product.desc_en;
  const hasPromo = product.promo_price && product.promo_price < product.price;
  const images = product.images && product.images.length ? product.images : [null];

  const shareUrl = `${BACKEND_URL}/api/og/product/${product.id}`;

  const onCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t("common.copied"));
    } catch {
      toast.error("Copy failed");
    }
  };

  const onShareWa = () => {
    const msg = `${name} — ${formatRM(product.promo_price || product.price)}\n${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const onAdd = () => {
    addItem(product, qty);
    toast.success(t("products.added"));
  };

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12" data-testid="product-detail-page">
      <Link
        to="/products"
        className="inline-flex items-center gap-1.5 text-sm text-[#4a6e77] hover:text-[#005870] transition-colors duration-200"
        data-testid="back-to-products"
      >
        <ArrowLeft size={14} /> {t("common.back")}
      </Link>

      <div className="mt-8 grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <div className="relative overflow-hidden rounded-md bg-[#f5f0e8] aspect-square">
            {images[activeImg] ? (
              <img
                src={resolveImage(images[activeImg])}
                alt={name}
                className="absolute inset-0 h-full w-full object-cover"
                data-testid="product-main-image"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center font-serif-cn text-4xl text-[#4a6e77]">
                {name}
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {images.map((im, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`h-16 w-16 rounded-md overflow-hidden border-2 transition-colors duration-200 ${
                    activeImg === i ? "border-[#005870]" : "border-transparent"
                  }`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={resolveImage(im)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:pt-4">
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">
            {t(`products.${product.category}`)}
          </div>
          <h1 className="mt-3 font-serif-cn text-3xl md:text-4xl text-[#003645] font-semibold leading-tight">
            {name}
          </h1>
          <div className="mt-1 text-sm text-[#4a6e77] tracking-wide">{otherName}</div>
          {product.sku && (
            <div className="mt-2 text-xs text-[#4a6e77] tracking-wider" data-testid="product-sku">
              SKU: <span className="font-mono">{product.sku}</span>
            </div>
          )}

          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-2xl font-semibold text-[#005870]">
              {formatRM(product.promo_price || product.price)}
            </span>
            {hasPromo && (
              <span className="text-sm text-[#4a6e77] line-through">
                {formatRM(product.price)}
              </span>
            )}
            <span
              className={`ml-2 text-xs px-2 py-1 rounded-full ${
                product.stock > 0
                  ? "bg-[#00868e]/10 text-[#00868e]"
                  : "bg-red-50 text-red-600"
              }`}
              data-testid="stock-badge"
            >
              {product.stock > 0 ? t("products.inStock") : t("products.outOfStock")}
            </span>
          </div>

          <p className="mt-6 text-[#4a6e77] leading-[1.9] font-serif-cn">{desc}</p>

          {/* Qty + Add to cart */}
          <div className="mt-10 flex items-center gap-4">
            <div className="inline-flex items-center border border-[#e5ebeb] rounded-full">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="h-11 w-11 flex items-center justify-center text-[#4a6e77] hover:text-[#005870] transition-colors duration-200"
                data-testid="qty-minus"
              >
                <Minus size={14} />
              </button>
              <span className="w-10 text-center font-medium text-[#003645]" data-testid="qty-value">
                {qty}
              </span>
              <button
                onClick={() => setQty(qty + 1)}
                className="h-11 w-11 flex items-center justify-center text-[#4a6e77] hover:text-[#005870] transition-colors duration-200"
                data-testid="qty-plus"
              >
                <Plus size={14} />
              </button>
            </div>

            <button
              onClick={onAdd}
              disabled={product.stock <= 0}
              className="flex-1 px-6 py-3 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-to-cart-btn"
            >
              {t("products.addToCart")}
            </button>

            <button
              onClick={() => setShareOpen((v) => !v)}
              className="h-12 w-12 flex items-center justify-center rounded-full border border-[#e5ebeb] text-[#4a6e77] hover:border-[#00868e] hover:text-[#00868e] transition-colors duration-200"
              aria-label="Share"
              data-testid="share-btn"
            >
              <Share2 size={16} />
            </button>
          </div>

          {shareOpen && (
            <div
              className="mt-4 p-4 rounded-md border border-[#e5ebeb] bg-white flex flex-col sm:flex-row gap-2"
              data-testid="share-menu"
            >
              <button
                onClick={onCopyLink}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-[#e5ebeb] text-sm text-[#003645] hover:border-[#00868e] hover:text-[#00868e] transition-colors duration-200"
                data-testid="share-copy-btn"
              >
                <Copy size={14} /> {t("products.shareCopy")}
              </button>
              <button
                onClick={onShareWa}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#25D366] text-white text-sm font-medium hover:opacity-90 transition-opacity duration-200"
                data-testid="share-wa-btn"
              >
                <MessageCircle size={14} /> {t("products.shareWa")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
